// askSmart-ui/src/pages/TOSP/components/UnitDetailsModal.tsx
import React from "react";
import { formatCurrency, formatSqFt } from "../format";
import { useTospData } from "../TospDataContext";
import {
  bmvaAnnualFee,
  bmvaMonthlyFee,
  bmvaRate,
  rollsForUnit,
  taxRollTotal,
  Unit,
} from "../types";

interface UnitDetailsModalProps {
  /** The unit to display, or null when closed. */
  unit: Unit | null;
  /** Resolves the unit's building id to a name. */
  buildingName: (buildingId: string) => string;
  onClose: () => void;
}

/** The per-year amounts, entered on the Tax Rolls tab. */
const TAX_YEAR_COLUMNS = ["Year", "Budget PA", "Interim", "Final", "Total PA"];

const money = (value: number | null): string => (value === null ? "—" : formatCurrency(value));

// Read-only view of a Unit, so an admin can check what was entered — including
// its tax rolls — without opening the edit form and risking a change.
export default function UnitDetailsModal({
  unit,
  buildingName,
  onClose,
}: UnitDetailsModalProps) {
  const { taxRolls, configuration } = useTospData();
  if (!unit) return null;

  // A unit can hold many rolls, so they're listed rather than shown as one
  // set of fields — C1 has ten.
  const rolls = rollsForUnit(taxRolls, unit.id);

  const rows: { label: string; value: string }[] = [
    { label: "Building", value: buildingName(unit.buildingId) },
    { label: "Suite #", value: unit.suiteNumber },
    { label: "Space Type", value: unit.spaceType },
    { label: "Occupancy SqFt", value: formatSqFt(unit.occupancySqFt) },
    { label: "BMVA SqFt", value: formatSqFt(unit.bmvaSqFt) },
    { label: "BMVA Rate / SqFt", value: formatCurrency(bmvaRate(configuration, unit)) },
    { label: "BMVA Annual Fee", value: formatCurrency(bmvaAnnualFee(unit, configuration)) },
    { label: "BMVA Monthly Fee", value: formatCurrency(bmvaMonthlyFee(unit, configuration)) },
    {
      label: "Tax Rolls",
      value: rolls.length
        ? rolls
            .map(
              (roll) =>
                `${roll.rollNumber}${
                  roll.unit ? ` (Plan ${roll.planNumber} L${roll.level} Unit ${roll.unit})` : ""
                }`,
            )
            .join(", ")
        : "— none assigned",
    },
  ];

  const taxYears = [...(unit.taxYears || [])].sort((a, b) => b.year - a.year);

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal tosp-modal-wide">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>Unit Details</h2>
        </div>
        <div className="smrtdocs-modal-body">
          {rows.map((row) => (
            <div key={row.label} className="tosp-detail-row">
              <div className="tosp-detail-label">{row.label}</div>
              <div className="tosp-detail-value">{row.value}</div>
            </div>
          ))}

          {/* The roll's identity sits with the unit's other details above;
              this is its assessed history, newest year first. */}
          <div className="tosp-section">
            <div className="tosp-section-header">
              <span className="tosp-section-title">Tax History</span>
            </div>

            {taxYears.length === 0 ? (
              <p className="tosp-field-hint">
                No tax years recorded. Open one on the Tax Rolls tab.
              </p>
            ) : (
              <div className="tosp-taxroll-scroll">
                <div className="tosp-taxroll-body">
                  <div className="tosp-taxroll-header tosp-taxroll-readonly">
                    {TAX_YEAR_COLUMNS.map((label) => (
                      <div key={label}>{label}</div>
                    ))}
                  </div>
                  {taxYears.map((taxYear) => (
                    <div
                      key={taxYear.id}
                      className="tosp-taxroll-row tosp-taxroll-readonly"
                    >
                      <div>{taxYear.year}</div>
                      <div>{money(taxYear.budgetPa)}</div>
                      <div>{money(taxYear.interim)}</div>
                      <div>{money(taxYear.final)}</div>
                      <div>{formatCurrency(taxRollTotal(taxYear))}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" className="btn-cancel" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
